import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useTheme } from '../contexts/ThemeContext';
import {
  ArrowUpRight, Sparkles, Rocket, FileText,
  AlertTriangle, Briefcase, Newspaper, ShieldAlert,
  Video, Trophy, Mail, Scale, ExternalLink, Bot,
  Clock, CheckCircle2, Send as SendIcon, Loader2,
  Search, Zap, MoreHorizontal, ChevronRight, Activity,
  TrendingUp, Shield, Eye, MailOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { getCampaigns, getMaterialTypes, getAgentConfigs, deleteCampaign, updateCampaign, retryCampaign } from '../lib/api';

// ─── Icon map for material types ───
const ICON_MAP = {
  'alert-triangle': AlertTriangle,
  'briefcase': Briefcase,
  'newspaper': Newspaper,
  'shield-alert': ShieldAlert,
  'rocket': Rocket,
  'video': Video,
  'trophy': Trophy,
  'file-text': FileText,
  'mail': Mail,
  'scale': Scale,
};

// ─── Status config ───
const STATUS_MAP = {
  draft: { label: 'QUEUED', color: 'hsl(215 20% 55%)', dot: 'bg-muted-foreground/50' },
  created: { label: 'QUEUED', color: 'hsl(215 20% 55%)', dot: 'bg-muted-foreground/50' },
  idle: { label: 'QUEUED', color: 'hsl(215 20% 55%)', dot: 'bg-muted-foreground/50' },
  pipeline_running: { label: 'PROCESSING', color: 'hsl(var(--brand))', dot: 'bg-[hsl(var(--brand))]' },
  error: { label: 'FAILED', color: 'hsl(var(--status-error))', dot: 'bg-[hsl(var(--status-error))]' },
  content_review: { label: 'AWAITING REVIEW', color: 'hsl(var(--status-warning))', dot: 'bg-[hsl(var(--status-warning))]' },
  review_needed: { label: 'AWAITING REVIEW', color: 'hsl(var(--status-warning))', dot: 'bg-[hsl(var(--status-warning))]' },
  content_draft_review: { label: 'CONTENT REVIEW', color: 'hsl(265 60% 60%)', dot: 'bg-violet-400' },
  approved: { label: 'APPROVED', color: 'hsl(var(--status-success))', dot: 'bg-[hsl(var(--status-success))]' },
  sent: { label: 'SENT', color: 'hsl(var(--brand))', dot: 'bg-[hsl(var(--brand))]' },
  scheduled: { label: 'SCHEDULED', color: 'hsl(var(--status-info))', dot: 'bg-[hsl(var(--status-info))]' },
  sending: { label: 'SENDING', color: 'hsl(var(--accent-warm))', dot: 'bg-[hsl(var(--accent-warm))]' },
};

// ─── Quick-start templates ───
const QUICK_STARTS = [
  { label: 'Urgent Alert', icon: AlertTriangle, text: 'Create an urgent threat alert about a new ransomware variant targeting critical infrastructure' },
  { label: 'Executive Brief', icon: Briefcase, text: 'Write an executive risk brief about a zero-day vulnerability affecting enterprise systems' },
  { label: 'Newsletter', icon: Newspaper, text: 'Compose a monthly cybersecurity newsletter covering the top threats and patches' },
  { label: 'Product Launch', icon: Rocket, text: 'Draft a product launch email for our new endpoint detection and response platform' },
];

// ─── Time ago ───
function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const now = new Date();
  const d = new Date(dateStr);
  const mins = Math.floor((now - d) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Task Row (Recent Tasks feed) ───
function TaskRow({ campaign, onClick, onRetry }) {
  const isStuck = campaign.status === 'pipeline_running' && campaign.updated_at && (Date.now() - new Date(campaign.updated_at).getTime() > 120000);
  const isError = campaign.status === 'error';
  const showRetry = isStuck || isError;
  const s = showRetry 
    ? (isError ? STATUS_MAP.error : { label: 'STUCK', color: 'hsl(var(--status-error))', dot: 'bg-[hsl(var(--status-error))]' })
    : (STATUS_MAP[campaign.status] || STATUS_MAP.draft);
  const topic = campaign.topic || campaign.name || 'Untitled Campaign';
  const id = campaign.id ? `CC-${campaign.id.slice(-5).toUpperCase()}` : '—';
  const Icon = ICON_MAP[campaign.material_icon] || Zap;

  return (
    <div
      data-testid={`task-row-${campaign.id}`}
      onClick={() => onClick(campaign)}
      className="flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.04] cursor-pointer transition-colors duration-100 border-b border-white/[0.04] last:border-b-0"
    >
      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-mono font-semibold text-muted-foreground/60">{id}</span>
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: s.color, backgroundColor: `${s.color}15` }}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {s.label}
          </span>
        </div>
        <p className="text-sm font-medium text-foreground truncate">{topic}</p>
      </div>
      {showRetry ? (
        <button
          data-testid={`task-retry-${campaign.id}`}
          onClick={(e) => { e.stopPropagation(); onRetry?.(campaign.id); }}
          className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[hsl(var(--accent-warm)/0.4)] bg-[hsl(var(--accent-warm)/0.1)] text-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.2)] transition-colors duration-150"
        >
          Retry
        </button>
      ) : (
        <span className="text-xs text-muted-foreground/60 flex-shrink-0">{timeAgo(campaign.created_at || campaign.updated_at)}</span>
      )}
    </div>
  );
}

// ─── Active Campaign Card ───
function CampaignCard({ campaign, onClick }) {
  const s = STATUS_MAP[campaign.status] || STATUS_MAP.draft;
  const topic = campaign.topic || campaign.name || 'Untitled';
  const id = campaign.id ? `CC-${campaign.id.slice(-5).toUpperCase()}` : '—';
  const Icon = ICON_MAP[campaign.material_icon] || ShieldAlert;

  return (
    <div
      data-testid={`campaign-card-${campaign.id}`}
      onClick={() => onClick(campaign)}
      className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-5 cursor-pointer
        hover:border-[hsl(var(--brand)/0.3)] hover:bg-white/[0.06] transition-colors duration-200
        motion-safe:hover:-translate-y-0.5 motion-safe:transition-transform"
      style={{ boxShadow: '0 1px 0 hsl(0 0% 0% / 0.55), 0 18px 55px hsl(0 0% 0% / 0.45)' }}
    >
      {/* Header: icon + name + external link */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand)/0.1)] border border-[hsl(var(--brand)/0.2)] flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-[hsl(var(--brand))]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{topic}</p>
            <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">{id}</p>
          </div>
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0 mt-1" />
      </div>

      {/* Metrics: STATUS / OPENS / CLICKS */}
      <div className="grid grid-cols-3 gap-2 mt-3" data-testid="campaign-card-metrics">
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/50 mb-0.5">Status</p>
          <div className="flex items-center justify-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            <span className="text-xs font-semibold" style={{ color: s.color }}>{s.label}</span>
          </div>
        </div>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/50 mb-0.5">Opens</p>
          <span className="text-xs font-mono font-semibold text-foreground">—</span>
        </div>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/50 mb-0.5">Clicks</p>
          <span className="text-xs font-mono font-semibold text-foreground">—</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── MAIN HOME TAB ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export default function HomeTab({ onCreateCampaign, onOpenCampaign }) {
  const { theme } = useTheme();
  const [campaigns, setCampaigns] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Composer state
  const [prompt, setPrompt] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('urgent_alert');
  const [selectedAgent, setSelectedAgent] = useState('auto');

  // ─── Data loading ───
  useEffect(() => {
    loadData();
    const interval = setInterval(loadCampaigns, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([loadCampaigns(), loadMaterialTypes(), loadAgents()]);
    setLoading(false);
  };

  const loadCampaigns = async () => {
    try {
      const res = await getCampaigns();
      setCampaigns(res.data?.campaigns || res.data || []);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
  };

  const loadMaterialTypes = async () => {
    try {
      const res = await getMaterialTypes();
      setMaterialTypes(res.data || []);
    } catch (err) {
      console.error('Failed to load material types:', err);
    }
  };

  const loadAgents = async () => {
    try {
      const res = await getAgentConfigs();
      const data = res.data?.agents || res.data || [];
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const handleRetry = async (campaignId) => {
    try {
      await retryCampaign(campaignId);
      toast.success('Pipeline restarted');
      await loadCampaigns();
    } catch (err) {
      toast.error('Retry failed');
    }
  };


  // ─── Create campaign ───
  const handleCreate = useCallback(() => {
    if (!prompt.trim()) return;
    onCreateCampaign(prompt.trim(), selectedMaterial);
    setPrompt('');
  }, [prompt, selectedMaterial, onCreateCampaign]);

  // ─── Derived data ───
  const runningCount = useMemo(() => campaigns.filter(c => c.status === 'pipeline_running').length, [campaigns]);
  const pendingReviewCount = useMemo(() => campaigns.filter(c => ['content_review', 'content_draft_review', 'review_needed'].includes(c.status)).length, [campaigns]);
  const sentCount = useMemo(() => campaigns.filter(c => c.status === 'sent' || c.status === 'sending').length, [campaigns]);

  // Recent tasks: last 8 by creation date
  const recentTasks = useMemo(() => {
    return [...campaigns]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 8);
  }, [campaigns]);

  // Active campaigns: all non-draft campaigns (matches header counter scope)
  const activeCampaigns = useMemo(() => {
    return campaigns
      .filter(c => ['pipeline_running', 'content_review', 'content_draft_review', 'review_needed', 'approved', 'sent', 'sending', 'scheduled'].includes(c.status))
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  }, [campaigns]);

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="home-tab">
      <ScrollArea className="flex-1">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* ═══════════════════════════════════════════════════════ */}
          {/* BRAND HEADER: Securado badge + real metrics            */}
          {/* ═══════════════════════════════════════════════════════ */}
          <div
            className="relative rounded-2xl overflow-hidden mb-8 border border-[hsl(var(--brand)/0.15)]"
            style={{
              background: 'linear-gradient(135deg, hsl(192 72% 10%) 0%, hsl(218 22% 9%) 60%, hsl(220 20% 8%) 100%)',
              boxShadow: '0 8px 40px hsl(192 72% 44% / 0.08)',
            }}
            data-testid="home-hero-banner"
          >
            {/* Noise texture */}
            <div
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-[0.06]"
              style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }}
            />
            <div className="relative z-10 px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Securado shield logo */}
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0" data-testid="securado-logo-badge">
                  <Shield className="w-6 h-6 text-[hsl(var(--brand))]" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="home-hero-title">
                    Mission Control
                  </h2>
                  <p className="text-sm text-white/50 mt-0.5">
                    Securado Campaign Intelligence Platform
                  </p>
                </div>
              </div>
              {/* Real operational metrics */}
              <div className="flex items-center gap-6 flex-shrink-0">
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Processing</p>
                  <p className="text-xl font-bold text-[hsl(var(--brand))] font-mono" data-testid="metric-processing">{runningCount}</p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Awaiting Review</p>
                  <p className="text-xl font-bold text-[hsl(var(--status-warning))] font-mono" data-testid="metric-review">{pendingReviewCount}</p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Sent</p>
                  <p className="text-xl font-bold text-[hsl(var(--status-success))] font-mono" data-testid="metric-sent">{sentCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* CAMPAIGN COMPOSER SECTION                              */}
          {/* ═══════════════════════════════════════════════════════ */}
          <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.05s' }} data-testid="home-deploy-section">
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-5"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="home-hero-heading"
            >
              What campaign are we launching?
            </h1>

            {/* Composer bar */}
            <div
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-2 mb-4"
              style={{ boxShadow: 'var(--shadow-elev-1)' }}
              data-testid="home-hero-composer"
            >
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 ml-1">
                <Mail className="w-5 h-5 text-muted-foreground" />
              </div>
              <Input
                data-testid="home-hero-composer-textarea"
                placeholder="Describe the campaign you want to launch..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                className="flex-1 h-11 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base placeholder:text-muted-foreground/40"
              />
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="h-9 w-[160px] text-xs bg-white/5 border-white/10 rounded-lg flex-shrink-0" data-testid="home-agent-select">
                  <Bot className="w-3 h-3 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="Auto (Pipeline)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (Full Pipeline)</SelectItem>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                className="h-10 px-5 gap-2 text-sm font-semibold rounded-xl bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] active:scale-[0.98] flex-shrink-0 transition-transform duration-150"
                onClick={handleCreate}
                disabled={!prompt.trim()}
                data-testid="home-send-button"
              >
                <Rocket className="w-4 h-4" /> Launch
              </Button>
            </div>

            {/* Template type pills */}
            <div className="flex flex-wrap gap-2" data-testid="home-quickstart-pills">
              {materialTypes.length > 0 ? (
                materialTypes.slice(0, 6).map(mt => {
                  const Icon = ICON_MAP[mt.icon] || Zap;
                  const isActive = selectedMaterial === mt.id;
                  return (
                    <button
                      key={mt.id}
                      data-testid={`home-material-tab-${mt.id}`}
                      onClick={() => {
                        setSelectedMaterial(mt.id);
                        // Also fill prompt with a template if empty
                        if (!prompt.trim()) {
                          const templates = {
                            'urgent_alert': 'Urgent security alert about a critical vulnerability targeting enterprise systems',
                            'executive_brief': 'Executive risk brief on the latest cyber threat landscape for C-suite leadership',
                            'security_newsletter': 'Monthly cybersecurity newsletter covering top threats, patches, and industry news',
                            'threat_intelligence': 'Threat intelligence report on emerging attack vectors and APT group activity',
                            'product_launch': 'Product launch announcement for our new cybersecurity solution',
                            'webinar_invitation': 'Webinar invitation for an upcoming cybersecurity masterclass',
                            'case_study': 'Customer success case study highlighting breach prevention results',
                            'compliance_update': 'Compliance update on new regulatory requirements and security standards',
                            'incident_response': 'Incident response advisory with immediate action steps',
                            'partner_announcement': 'Strategic partnership announcement for enhanced security offerings',
                          };
                          setPrompt(templates[mt.id] || `Create a ${mt.name} campaign`);
                        }
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-150 ${
                        isActive
                          ? 'bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand))] border-[hsl(var(--brand)/0.3)] shadow-sm'
                          : 'bg-white/5 text-muted-foreground border-transparent hover:bg-white/[0.08] hover:text-foreground'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {mt.name}
                    </button>
                  );
                })
              ) : (
                QUICK_STARTS.map((qs, idx) => (
                  <button
                    key={idx}
                    data-testid={`home-quickstart-pill-${idx}`}
                    onClick={() => setPrompt(qs.text)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.08] hover:text-foreground transition-colors duration-150"
                  >
                    <qs.icon className="w-3 h-3" />
                    {qs.label}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* SPLIT LAYOUT: RECENT TASKS + ACTIVE CAMPAIGNS         */}
          {/* ═══════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>

            {/* LEFT: Recent Tasks (3 cols) */}
            <div className="lg:col-span-3" data-testid="recent-tasks-section">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold">Recent Tasks</p>
                <span className="text-[11px] text-muted-foreground/60">{campaigns.length} total</span>
              </div>
              <div
                className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] overflow-hidden"
                style={{ boxShadow: 'var(--shadow-elev-1)' }}
              >
                {recentTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Search className="w-8 h-8 text-muted-foreground/20 mb-3" />
                    <p className="text-sm text-muted-foreground">No tasks yet. Launch your first campaign above.</p>
                  </div>
                ) : (
                  recentTasks.map(c => (
                    <TaskRow key={c.id} campaign={c} onClick={onOpenCampaign} onRetry={handleRetry} />
                  ))
                )}
              </div>
            </div>

            {/* RIGHT: Active Campaigns (2 cols) */}
            <div className="lg:col-span-2" data-testid="active-campaigns-section">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold">Active Campaigns</p>
                <div className="flex items-center gap-1">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--status-success))] opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--status-success))]" />
                  </span>
                  <span className="text-[10px] text-[hsl(var(--status-success))] font-medium">{activeCampaigns.length} active</span>
                </div>
              </div>
              <div className="space-y-4 stagger-children">
                {activeCampaigns.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] flex flex-col items-center justify-center py-16 text-center" style={{ boxShadow: 'var(--shadow-elev-1)' }}>
                    <Activity className="w-8 h-8 text-muted-foreground/20 mb-3" />
                    <p className="text-sm text-muted-foreground">No active campaigns yet.</p>
                  </div>
                ) : (
                  activeCampaigns.map(c => (
                    <CampaignCard key={c.id} campaign={c} onClick={onOpenCampaign} />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Securado Copyright Footer */}
          <div className="mt-8 pt-4 border-t border-white/[0.04] flex items-center justify-center gap-2" data-testid="home-securado-footer">
            <svg className="w-4 h-4 text-muted-foreground/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span className="text-[11px] text-muted-foreground/30">
              Securado Campaign Intelligence Platform
            </span>
          </div>

        </div>
      </ScrollArea>
    </div>
  );
}
